import React from 'react'

function normalizeMessageId(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(String(value || '').trim())
  if (!Number.isFinite(n) || n <= 0) return null
  return Math.floor(n)
}

type Params = {
  open: boolean
  focusMessageId?: number | string | null
  messages: { id: number }[]
  containerRef: React.RefObject<HTMLElement | null>
  highlightDurationMs?: number
}

export function useCommunityMessageFocus({
  open,
  focusMessageId,
  messages,
  containerRef,
  highlightDurationMs = 4000,
}: Params) {
  const focusId = normalizeMessageId(focusMessageId)
  const pendingFocusRef = React.useRef<number | null>(null)
  const pendingSinceRef = React.useRef<number | null>(null)
  const lastFocusedRef = React.useRef<number | null>(null)
  const [highlightId, setHighlightId] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (!open) {
      pendingFocusRef.current = null
      pendingSinceRef.current = null
      lastFocusedRef.current = null
      setHighlightId(null)
      return
    }

    if (!focusId) return
    if (lastFocusedRef.current === focusId) return

    pendingFocusRef.current = focusId
    pendingSinceRef.current = Date.now()
  }, [focusId, open])

  React.useEffect(() => {
    if (!open) return

    const pendingId = pendingFocusRef.current
    if (!pendingId) return

    const container = containerRef.current
    if (!container) return

    const selector = `[data-community-message-id="${pendingId}"]`
    const el = container.querySelector(selector) as HTMLElement | null

    if (!el) {
      const since = pendingSinceRef.current
      if (since && Date.now() - since > 3500) {
        pendingFocusRef.current = null
        pendingSinceRef.current = null
      }
      return
    }

    const focus = () => {
      try {
        el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' })
      } catch {
        try {
          el.scrollIntoView()
        } catch {
          // ignore
        }
      }

      lastFocusedRef.current = pendingId
      pendingFocusRef.current = null
      pendingSinceRef.current = null
      setHighlightId(pendingId)
    }

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      const handle = window.requestAnimationFrame(focus)
      return () => window.cancelAnimationFrame(handle)
    }

    focus()
  }, [containerRef, messages.length, open])

  React.useEffect(() => {
    if (!open) return
    if (!highlightId) return
    if (!highlightDurationMs || highlightDurationMs <= 0) return

    const t = window.setTimeout(() => {
      setHighlightId((cur) => (cur === highlightId ? null : cur))
    }, highlightDurationMs)

    return () => window.clearTimeout(t)
  }, [highlightDurationMs, highlightId, open])

  return {
    focusId,
    highlightedMessageId: highlightId,
    disableAutoScrollToBottom: !!focusId,
  }
}

